import { Point, Rect, NullChecker } from '@pacem/pacem-foundation';
import { Vector } from './linear-algebra';
import { Delaunay } from './algorithms/delaunay';
const RAD2DEG = 180.0 / Math.PI;
const RAD_ROUND = 2 * Math.PI;
const PRECISION = 12; // default epsilon 1e-12 checking proximity (not used for roundoffs)
;
function isPoint(p) {
    return Point.isPoint(p);
}
function isSegment(s) {
    return s != null && Array.isArray(s) && s.length === 2
        && isPoint(s[0]) && isPoint(s[1]);
}
export class Utils {
    /**
     * Computes the slope (in radians) of the segment joining two points.
     * @param p1 Point 1
     * @param p2 Point 2
     */
    static slopeRad(p1, p2) {
        return Math.atan2(p2.y - p1.y, p2.x - p1.x);
    }
    /**
     * Computes the slope (in radians) of the segment joining two points and returns a strictly positive value (0 to 2*pi).
     * @param p1 Point 1
     * @param p2 Point 2
     */
    static slopeRad2(p1, p2) {
        return (RAD_ROUND + Utils.slopeRad(p1, p2)) % RAD_ROUND;
    }
    /**
     * Computes the slope (in degrees) of the segment joining two points.
     * @param p1 Point 1
     * @param p2 Point 2
     */
    static slopeDeg(p1, p2) {
        return Utils.slopeRad(p1, p2) * RAD2DEG;
    }
    /**
     * Computes the slope (in degrees) of the segment joining two points and returns a strictly positive value (0 to 360).
     * @param p1 Point 1
     * @param p2 Point 2
     */
    static slopeDeg2(p1, p2) {
        return (360 + Utils.slopeDeg(p1, p2)) % 360;
    }
    static intersect(arg1, arg2, ...args) {
        if (Array.isArray(arg1) && Array.isArray(arg2)) {
            return Utils._intersectSegments(arg1, arg2, true);
        }
        else if (Rect.isRect(arg1)) {
            return Utils._intersectRects(...arguments);
        }
        else if ('vertices' in arg1) {
            return Utils._mergePolygons(arg1, arg2, 'intersect')?.polygons ?? null;
        }
        else {
            return null;
        }
    }
    static boundingBox(vertices) {
        let xmin = Number.MAX_VALUE, ymin = Number.MAX_VALUE, xmax = -Number.MAX_VALUE, ymax = -Number.MAX_VALUE;
        const l = vertices.length;
        for (let v of vertices) {
            xmin = Math.min(xmin, v.x);
            ymin = Math.min(ymin, v.y);
            xmax = Math.max(xmax, v.x);
            ymax = Math.max(ymax, v.y);
        }
        return { x: xmin, y: ymin, width: xmax - xmin, height: ymax - ymin };
    }
    static combinePolygons(polygon1, polygon2, operator) {
        return Utils._mergePolygons(polygon1, polygon2, operator);
    }
    static _mergePolygons(polygon1, polygon2, operator) {
        if (NullChecker.isNullOrEmpty(polygon1?.vertices) || NullChecker.isNullOrEmpty(polygon2?.vertices)) {
            return null;
        }
        // look for sudden exit
        // 2 contains 1
        const contained1 = polygon1.vertices.filter(v => Utils.inPolygon(v, polygon2.vertices));
        if (polygon1.vertices.length == contained1.length) {
            return {
                polygons: operator === 'intersect' ? [polygon1] : /* hole in difference */ [polygon2]
            };
        }
        // 1 contains 2
        const contained2 = polygon2.vertices.filter(v => Utils.inPolygon(v, polygon1.vertices));
        if (polygon2.vertices.length === contained2.length) {
            return {
                polygons: operator === 'intersect' ? [polygon2] : /* hole in difference */ [polygon1]
            };
        }
        // bboxes do not intersect
        const bbox1 = Utils.boundingBox(polygon1.vertices), bbox2 = Utils.boundingBox(polygon2.vertices);
        const rect = Utils.intersect(bbox1, bbox2);
        if (operator !== 'union' && (!Rect.isRect(rect) || !(rect.width > 0) || !(rect.height > 0))) {
            return null; // { polygons: [] };
        }
        const { vertices: vertices1 } = polygon1, { vertices: vertices2 } = polygon2;
        if (vertices1.length < 3 || vertices2.length < 3) {
            return null;
        }
        // steps:
        // 1. compute the intersection points between the two polygon's edges,
        // 2. triangulate all the vertices (original + intersection points)
        // 3. filter the triangles whose barycenter is not within both the original polygons
        // 4. return the vertices of the remaining triangles as the intersection polygon vertices
        const sideBuilderCallback = (v, j, arr) => [v, arr[(j + 1) % arr.length]];
        const verticesSortCallback = (a, b) => a.x === b.x ? a.y - b.y : a.x - b.x;
        const edges1 = vertices1.map(sideBuilderCallback), edges2 = vertices2.map(sideBuilderCallback);
        const outputVertices = vertices1.concat(vertices2);
        const memoizer = new Map();
        const ensureMemoizer = e => {
            if (!memoizer.has(e)) {
                const retval = [];
                memoizer.set(e, retval);
            }
            return memoizer.get(e);
        };
        // loop to find edge intersections
        let thereAreIntersections = false;
        for (let edge1 of edges1) {
            const additionalVertices1 = ensureMemoizer(edge1);
            for (let edge2 of edges2) {
                const additionalVertices2 = ensureMemoizer(edge2);
                const intersection = Utils.intersect(edge1, edge2);
                if (!NullChecker.isNull(intersection)) {
                    // found intersection
                    thereAreIntersections = true;
                    outputVertices.push(intersection);
                    additionalVertices1.push(intersection);
                    additionalVertices2.push(intersection);
                }
            }
        }
        // if no point is contained in each other's polygon AND additionalVertices is empty AND 'intersect' => exit
        if (contained1.length === 0 && contained2.length === 0 && !thereAreIntersections) {
            switch (operator) {
                case 'union':
                    return { polygons: [polygon1, polygon2] };
                default:
                    return null; // { polygons: [] };
            }
        }
        const constraints = [];
        for (let edge of edges1.concat(edges2)) {
            const additionalVertices = ensureMemoizer(edge);
            if (NullChecker.isNullOrEmpty(additionalVertices)) {
                constraints.push(edge);
            }
            else {
                // split the edge into segments by the additional vertices
                const totalVertices = edge.concat(additionalVertices).sort(verticesSortCallback);
                for (let j = 0; j < totalVertices.length - 1; j++) {
                    constraints.push([totalVertices[j], totalVertices[j + 1]]);
                }
            }
        }
        // simplify by removing coinciding vertices
        var dirty = false;
        var mesh;
        do {
            outputVertices.sort(verticesSortCallback);
            outputVertices.filter((v, i, arr) => { return i === 0 || !Utils.areClose(v, arr[i - 1]); });
            // triangulate
            mesh = Utils.mesh(outputVertices);
            const { vertices, triangleIndices } = mesh;
            dirty = false;
            // enforce constraints
            for (let j = 0; j < triangleIndices.length; j += 3) {
                const indexA = triangleIndices[j], indexB = triangleIndices[j + 1], indexC = triangleIndices[j + 2];
                const A = vertices[indexA], B = vertices[indexB], C = vertices[indexC];
                const sides = [[A, B], [B, C], [C, A]];
                for (let constraint of constraints) {
                    for (let side of sides) {
                        const consecutive = Utils.areClose(side[0], constraint[0])
                            || Utils.areClose(side[1], constraint[1])
                            || Utils.areClose(side[0], constraint[1])
                            || Utils.areClose(side[1], constraint[0]);
                        const intersection = consecutive ? null : Utils.intersect(side, constraint);
                        if (!NullChecker.isNull(intersection) && outputVertices.findIndex(p => Utils.areClose(p, intersection, 4)) === -1) {
                            // pick triangle adjacent to the intesected side
                            dirty = true;
                            outputVertices.push(intersection);
                            //break;
                        }
                    }
                }
            }
        } while (dirty);
        const { vertices, triangleIndices } = mesh;
        // loop through triangles and filter by barycenter
        const triangles = [];
        for (let j = 0; j < triangleIndices.length; j += 3) {
            const jA = j, jB = j + 1, jC = j + 2;
            const A = vertices[triangleIndices[jA]], B = vertices[triangleIndices[jB]], C = vertices[triangleIndices[jC]];
            const O = { x: (A.x + B.x + C.x) / 3, y: (A.y + B.y + C.y) / 3 };
            const in1 = Utils.inPolygon(O, vertices1), in2 = Utils.inPolygon(O, vertices2);
            if (in1 && in2 && operator === 'intersect'
                || (in1 || in2) && operator === 'union'
                || (in1 && !in2 || !in1 && in2) && operator === 'difference') {
                triangles.push([A, B, C]);
            }
        }
        // return the unique vertices of the remaining triangles as the intersection polygon vertices
        // 1. list all edges
        const edges = triangles.flatMap(([A, B, C]) => [[A, B], [B, C], [C, A]]);
        // 2. filter by uniqueness
        const uniqueEdges = [];
        for (let j = 0; j < edges.length; j++) {
            const edge = edges[j];
            if (j == 0) {
                uniqueEdges.push(edge);
                continue;
            }
            const alreadyIn = uniqueEdges.findIndex(e => Utils.areClose(e[0], edge[0]) && Utils.areClose(e[1], edge[1]) || Utils.areClose(e[0], edge[1]) && Utils.areClose(e[1], edge[0]));
            if (alreadyIn === -1) {
                uniqueEdges.push(edge);
            }
            else {
                uniqueEdges.splice(alreadyIn, 1);
            }
        }
        if (uniqueEdges.length < 3) {
            return null;
        }
        const polygons = [];
        const cleanupPolygon = (vertices) => {
            // check that the starting vertex is not repeated
            const { length } = vertices;
            const repeated = Utils.areClose(vertices[0], vertices[length - 1]);
            const retval = vertices.slice(0, repeated ? (length - 1) : length);
            // check sneaky starting in-segment vertex
            if (retval.length > 3 && Utils.inSegment(retval[0], [retval[1], retval[retval.length - 1]])) {
                retval.shift();
            }
            return retval;
        };
        // 3. order the boundary vertices by following the edges
        const [P0, P1] = uniqueEdges.shift();
        const boundaryVertices = [P0, P1];
        while (uniqueEdges.length > 1) {
            const lastVertex = boundaryVertices[boundaryVertices.length - 1];
            const nextEdgeIndex = uniqueEdges.findIndex(e => Utils.areClose(e[0], lastVertex) || Utils.areClose(e[1], lastVertex));
            // manage to return multiple polygons in case of multiple disconnected intersections
            // nextVertex could be null but uniqueEdges not empty, then move to another polygon to yield.
            if (nextEdgeIndex === -1) {
                // check that the starting vertex is not repeated
                polygons.push({ vertices: cleanupPolygon(boundaryVertices.splice(0)) });
                // start next polygon
                const [P2, P3] = uniqueEdges.shift();
                boundaryVertices.push(P2, P3);
            }
            else {
                const [nextEdge] = uniqueEdges.splice(nextEdgeIndex, 1);
                const nextVertex = Utils.areClose(nextEdge[0], lastVertex) ? nextEdge[1] : (Utils.areClose(nextEdge[1], lastVertex) ? nextEdge[0] : null);
                // cleanup past in-segment vertices
                if (boundaryVertices.length > 1 && Utils.inSegment(boundaryVertices[boundaryVertices.length - 1], [boundaryVertices[boundaryVertices.length - 2], nextVertex])) {
                    boundaryVertices.splice(boundaryVertices.length - 1, 1, nextVertex);
                }
                else {
                    boundaryVertices.push(nextVertex);
                }
            }
        }
        polygons.push({ vertices: cleanupPolygon(boundaryVertices) });
        return {
            polygons, mesh, triangles: triangles.map(vertices => { return { vertices }; })
        };
    }
    static mesh(point, ...points) {
        const vertices = [].concat(point).concat(points), triangleIndices = Delaunay.triangulate(vertices);
        return { vertices, triangleIndices };
    }
    static intersectLines(segment1, segment2, m2, q2) {
        let s1, s2;
        // overload 2 => m+q in pairs, reset as overload 1
        if (Array.isArray(segment1) && Array.isArray(segment2)
            && segment1.length === 2 && segment2.length === 2
            && typeof segment1[0] === 'number' && typeof segment2[0] === 'number' && typeof segment1[1] === 'number' && typeof segment2[1] === 'number') {
            // var type switch, instructions' order matter!
            m2 = segment2[0];
            q2 = segment2[1];
            segment2 = segment1[1];
            segment1 = segment1[0];
        }
        // overload 1?
        if (typeof segment1 === 'number' && typeof segment2 === 'number') {
            const m1 = segment1, q1 = segment2;
            // dummy segments
            s1 = [
                { x: 0, y: q1 },
                { x: 1, y: m1 + q1 }
            ];
            s2 = [
                { x: 0, y: q2 },
                { x: 1, y: m2 + q2 }
            ];
        }
        // overload 0
        else {
            s1 = segment1;
            s2 = segment2;
        }
        return Utils._intersectSegments(s1, s2, false);
    }
    static cramer(l1, l2) {
        if (Array.isArray(l1)) {
            l1 = { a: l1[0], b: l1[1], c: l1[2] };
        }
        if (Array.isArray(l2)) {
            l2 = { a: l2[0], b: l2[1], c: l2[2] };
        }
        let xP = (l1.b * l2.c - l1.c * l2.b) / (l1.b * l2.a - l1.a * l2.b), yP = (l1.c * l2.a - l1.a * l2.c) / (l1.b * l2.a - l1.a * l2.b);
        return { x: -xP.roundoff(), y: -yP.roundoff() };
    }
    static mq(arg0, p2) {
        let p1;
        if (isPoint(arg0)) {
            p1 = arg0;
        }
        else {
            p1 = arg0[0];
            p2 = arg0[1];
        }
        if (p1.x === p2.x) {
            return [Number.NaN, Number.NaN];
        }
        const m = (p2.y - p1.y) / (p2.x - p1.x);
        const q = p1.y - m * p1.x;
        return [m, q];
    }
    static _intersectSegments(segment1, segment2, excludeProjection) {
        // do bounding boxes intersect?
        const A = segment1[0], B = segment1[1], C = segment2[0], D = segment2[1], xAB = Math.min(A.x, B.x), yAB = Math.min(A.y, B.y), XAB = Math.max(A.x, B.x), wAB = XAB - xAB, YAB = Math.max(A.y, B.y), hAB = YAB - yAB, xCD = Math.min(C.x, D.x), yCD = Math.min(C.y, D.y), XCD = Math.max(C.x, D.x), wCD = XCD - xCD, YCD = Math.max(C.y, D.y), hCD = YCD - yCD;
        if (wAB === 0 && wCD === 0) {
            // both vertical segments (null or infinite intersections) return null
            return null;
        }
        if (hAB === 0 && hCD === 0) {
            // both horizontal segments (null or infinite intersections) return null
            return null;
        }
        // dry
        const outOfXBounds = (p) => {
            return p.x < xCD || p.x > XCD || p.x < xAB || p.x > XAB;
        };
        const outOfYBounds = (p) => {
            return p.y < yCD || p.y > YCD || p.y < yAB || p.y > YAB;
        };
        var retval = null;
        if (wAB === 0) {
            if (excludeProjection && (xCD > xAB || XCD < XAB)) {
                return null;
            }
            const mqCD = Utils.mq(C, D);
            retval = Utils.cramer([1, 0, -xAB], [mqCD[0], -1, mqCD[1]]);
            if (excludeProjection && outOfYBounds(retval)) {
                retval = null;
            }
        }
        else if (wCD === 0) {
            if (excludeProjection && (xAB > xCD || XAB < XCD)) {
                return null;
            }
            const mqAB = Utils.mq(A, B);
            retval = Utils.cramer([mqAB[0], -1, mqAB[1]], [1, 0, -xCD]);
            if (excludeProjection && outOfYBounds(retval)) {
                retval = null;
            }
        }
        else if (hAB === 0) {
            if (excludeProjection && (yCD > yAB || YCD < YAB)) {
                return null;
            }
            const mqCD = Utils.mq(C, D);
            retval = Utils.cramer([0, 1, -yAB], [mqCD[0], -1, mqCD[1]]);
            if (excludeProjection && outOfXBounds(retval)) {
                retval = null;
            }
        }
        else if (hCD === 0) {
            if (excludeProjection && (yAB > yCD || YAB < YCD)) {
                return null;
            }
            const mqAB = Utils.mq(A, B);
            retval = Utils.cramer([mqAB[0], -1, mqAB[1]], [0, 1, -yCD]);
            if (excludeProjection && outOfXBounds(retval)) {
                retval = null;
            }
        }
        else {
            let intersection;
            if (!excludeProjection ||
                ((intersection = Utils._intersectRects({ x: xAB, y: yAB, width: wAB, height: hAB }, { x: xCD, y: yCD, width: wCD, height: hCD }))
                    && intersection.width > 0
                    && intersection.height > 0)) {
                // cramer's rule
                const mqAB = Utils.mq(A, B), mqCD = Utils.mq(C, D);
                const mAB = mqAB[0], mCD = mqCD[0], qAB = mqAB[1], qCD = mqCD[1];
                // parallel os superposed segments yield null
                if (mAB !== mCD) {
                    retval = Utils.cramer([mAB, -1, qAB], [mCD, -1, qCD]);
                    let xR = retval.x, yR = retval.y;
                    if (excludeProjection
                        && (xR < intersection.x || xR > (intersection.x + intersection.width)
                            || yR < intersection.y || yR > (intersection.y + intersection.height))) {
                        retval = null;
                    }
                }
            }
        }
        if (retval === null) {
            return null;
        }
        return { x: retval.x, y: retval.y };
    }
    static _intersectRects(...args) {
        return Rect.intersect(...args);
    }
    static _areClose(p1, p2, precision = PRECISION) {
        return Math.abs(p1.x - p2.x).isCloseTo(0, precision) && Math.abs(p1.y - p2.y).isCloseTo(0, precision);
    }
    static areClose(p1, p2, precision = PRECISION) {
        return Utils._areClose(p1, p2, precision);
    }
    /**
     * Returns the dot product between two vectors/points.
     * @param v1
     * @param v2
     */
    static dot(v1, v2) {
        return Vector.dot(v1, v2);
    }
    /**
     * Returns the cross product between two vectors/points (magnitude along the z axis).
     * @param v1
     * @param v2
     */
    static cross(v1, v2) {
        return Vector.cross(v1, v2);
    }
    static distance(p1, arg2) {
        if (isSegment(p1)) {
            return Point.distance(p1[0], p1[1]);
        }
        else if (isPoint(arg2)) {
            return Point.distance(p1, arg2);
        }
        else {
            const m = arg2[0], q = arg2[1];
            return Math.abs(m * p1.x - p1.y + q) / Math.sqrt(Math.pow(m, 2) + 1);
        }
    }
    static _pointSegmentDistance(p, segment) {
        const v1 = Vector.from(segment[0], segment[1]), v2 = Vector.from(segment[0], p);
        return Utils.cross(v1, v2);
    }
    static isWithin(p, segment, clockwise, precision = PRECISION) {
        const cross = Utils._pointSegmentDistance(p, segment);
        if (cross.isCloseTo(0, precision)) {
            return false;
        }
        return clockwise ? cross < 0 : cross > 0;
    }
    static isBeyond(p, segment, clockwise, precision = PRECISION) {
        return Utils.isWithin(p, segment, !clockwise, precision);
    }
    static inLine(p, segment, precision = PRECISION) {
        const cross = Utils._pointSegmentDistance(p, segment);
        return cross.isCloseTo(0, precision);
    }
    static inSegment(p, segment, precision = PRECISION) {
        const minx = Math.min(segment[0].x, segment[1].x), maxx = Math.max(segment[0].x, segment[1].x), miny = Math.min(segment[0].y, segment[1].y), maxy = Math.max(segment[0].y, segment[1].y);
        return p.x >= minx && p.x <= maxx && p.y >= miny && p.y <= maxy && Utils.inLine(p, segment, precision);
    }
    static inTriangle(p, triangle) {
        let last;
        for (let j = 0; j < 3; j++) {
            const p1 = triangle[j], p2 = triangle[(j + 1) % 3], v1 /* vector */ = Point.subtract(p2, p), v2 = Point.subtract(p1, p), current = Utils.cross(v1, v2);
            if (j > 0 && (current * last) <= 0) {
                // in the current 'triangle' we face a different direction,
                // then exit (:=not in polygon)
                return false;
            }
            last = current;
        }
        return true;
    }
    static inPolygon(p, vertices, precision = PRECISION) {
        if (!(vertices?.length >= 3)) {
            throw `Not enough vertices`;
        }
        const length = vertices.length;
        if (length === 3) {
            return Utils.inTriangle(p, [vertices[0], vertices[1], vertices[2]]);
        }
        if (precision > 0) {
            vertices = vertices.map(v => { return { x: v.x.roundoff(precision), y: v.y.roundoff(precision) }; });
            p = { x: p.x.roundoff(precision), y: p.y.roundoff(precision) };
        }
        // true polygon (might be concave, hulled, ...)
        // 1. preliminary bbox check
        let minx = Number.MAX_VALUE, miny = Number.MAX_VALUE, maxx = Number.MIN_VALUE, maxy = Number.MIN_VALUE;
        for (let j = 0; j < length; j++) {
            const v = vertices[j];
            minx = Math.min(minx, v.x);
            miny = Math.min(miny, v.y);
            maxx = Math.max(maxx, v.x);
            maxy = Math.max(maxy, v.y);
        }
        if (p.x < minx || p.x > maxx || p.y < miny || p.y > maxy) {
            return false;
        }
        // 2. intersections
        const /* dummy point 'left' to the left-most point of the shape and haivng the same 'y' of the target point */ outerPoint = { x: minx - 1, y: p.y }, 
        /* horizontal segment that binds the provided point with the outside one */ test = [outerPoint, p];
        let intersections = 0;
        for (let j = 0; j < length; j++) {
            const p1 = vertices[j], p2 = vertices[(j + 1) % length], side = [p1, p2];
            // edge case
            if (p1.y === p.y) {
                const p0 = vertices[(j - 1 + length) % length];
                // not on the same side (above or below)? then counts as an intersection
                if (p.x > p1.x && (p0.y - p.y) * (p2.y - p.y) < 0) {
                    intersections++;
                }
            }
            else if (p2.y === p.y) {
                // already handled at previous statement one loop ago, skip
                continue;
            }
            else {
                // straddle testing
                if (Utils.intersect(side, test) != null) {
                    intersections++;
                }
            }
        }
        return intersections % 2 === 1;
    }
    static area(point, ...points) {
        const pts = [].concat(point).concat(points);
        // shoelace algorithm
        let retval = 0;
        const length = pts.length;
        if (length >= 3) {
            for (let j = 0; j < pts.length; j++) {
                const { x: xj, y: yj } = pts[j], next = j === (length - 1) ? 0 : j + 1, { x: xnext, y: ynext } = pts[next];
                retval += (xj * ynext - yj * xnext);
            }
            retval *= .5;
        }
        return Math.abs(retval.roundoff());
    }
    static convexHull(point, ...points) {
        const pts = [].concat(point).concat(points);
        if (!(pts?.length > 1)) {
            return pts?.slice() ?? [];
        }
        const sorted = pts
            .map(p => ({ x: p.x, y: p.y }))
            .sort((a, b) => a.x === b.x ? a.y - b.y : a.x - b.x);
        const unique = [];
        for (const p of sorted) {
            const last = unique[unique.length - 1];
            if (!last || last.x !== p.x || last.y !== p.y) {
                unique.push(p);
            }
        }
        if (unique.length <= 2) {
            return unique;
        }
        const cross = (o, a, b) => {
            return (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
        };
        const lower = [];
        for (const p of unique) {
            while (lower.length >= 2
                && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) {
                lower.pop();
            }
            lower.push(p);
        }
        const upper = [];
        for (let j = unique.length - 1; j >= 0; j--) {
            const p = unique[j];
            while (upper.length >= 2
                && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) {
                upper.pop();
            }
            upper.push(p);
        }
        lower.pop();
        upper.pop();
        return lower.concat(upper);
    }
}
// }
